import jsPDF from 'jspdf';

// Carga el logo desde public y lo convierte a dataURL
async function loadLogoDataUrl() {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/lux_logo.PNG';
    } catch (e) {
      resolve(null);
    }
  });
}

/**
 * Genera un PDF del reporte a partir de datos (no imagen) y soporta paginación.
 * rows: array de filas con campos usados en la pantalla
 * totalesPorSeccion, totalesPorSerie: arrays de totales
 */
export async function generateReporteIngresosPDF({
  rows = [],
  totalesPorSeccion = [],
  totalesPorSerie = [],
  totalesPorConcepto = [],
  ingresoTotalDia = 0,
  title = 'Reporte de ingresos',
  fechaRango = '',
  descargar = true,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const usableWidth = pageWidth - margin * 2;

  const logoDataUrl = await loadLogoDataUrl();

  const headerHeight = 60;
  const footerHeight = 30;
  const rowHeight = 18;

  // Column definitions (aprox, ajustan al ancho usable)
  const cols = [
    { key: 'no', label: 'No.', width: 48 },
    { key: 'fecha', label: 'Fecha', width: 85 },
    { key: 'matricula', label: 'Matrícula', width: 70 },
    { key: 'nombre', label: 'Nombre', width: 190 }, // reducido
    { key: 'campus', label: 'Campus', width: 100 },  // aumentado
    // { key: 'grupo', label: 'Grupo', width: 65 },    // aumentado (oculta)
    { key: 'periodo', label: 'Periodo/Cobranza', width: 160 }, // aumentado
    { key: 'cnc', label: 'Cnc', width: 100 },
    { key: 'formaPago', label: 'Fm.Pag', width: 85 },
    { key: 'estado_pago', label: 'Estatus', width: 90 },
    { key: 'folio', label: 'Folio', width: 105 },
    { key: 'observaciones', label: 'Observaciones', width: 120 },
    { key: 'cargo', label: 'Cargo', width: 60 },
    { key: 'recargo', label: 'Recargo', width: 60 },
    { key: 'comision', label: 'Comisión 2%', width: 96 },
    { key: 'importe', label: 'Importe', width: 70 },
  ];
  // Escalar columnas para ocupar el 100% del ancho utilizable (solo una vez aquí)
  {
    const totalColsWidth = cols.reduce((s, c) => s + c.width, 0);
    const scaleX = usableWidth / totalColsWidth;
    cols.forEach((c) => {
      c.width = Math.floor(c.width * scaleX);
    });
  }

  // Scale columns if total width differs from usableWidth
  const totalColsWidth = cols.reduce((s, c) => s + c.width, 0);
  const scaleX = usableWidth / totalColsWidth;
  cols.forEach((c) => {
    c.width = Math.floor(c.width * scaleX);
  });

  const drawHeader = (includeTableHeader = true) => {
    let y = margin;
    // logo
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', margin, y, 52, 52); // Aumenta el tamaño del logo
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Centro De Estudios Superiores CMP, A.C.', pageWidth / 2, y + 10, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Ave. Venustiano Carranza Nte. No.Ext. 614 CENTRO CP 64000 MONTERREY, NUEVO LEÓN', pageWidth / 2, y + 26, { align: 'center' });

    doc.setFontSize(10);
    doc.text(title + (fechaRango ? ` — ${fechaRango}` : ''), pageWidth / 2, y + 46, { align: 'center' });

    // Encabezado de la tabla principal (opcional)
    const extraSpace = 44; // Más espacio extra entre encabezado y tabla
    if (includeTableHeader) {
      const thY = margin + headerHeight - 18 + extraSpace;
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, thY - 12, usableWidth, 16, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      let x = margin + 4;
      cols.forEach((col) => {
        doc.text(col.label, x, thY, { baseline: 'middle' });
        x += col.width;
      });
      return thY + 6;
    }

    // Si no se incluye encabezado de tabla, devolver una posición debajo del encabezado principal
    return margin + headerHeight + extraSpace;
  };

  // Start first page
  let currentY = drawHeader();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);

  const maxY = pageHeight - margin - footerHeight;

  const drawRow = (row, idx) => {
    let x = margin + 4;
    const cellPadding = 4; // padding horizontal extra
    // Prepara los textos de cada columna, aplicando splitTextToSize donde es necesario
    const cellData = [
      idx + 1,
      row.fecha || '',
      row.matricula || '',
      (row.nombre || '').toUpperCase().substring(0, 80),
      row.campus || '',
      // row.grupo || '', // oculto
      row.periodo && row.cobranza ? `${row.periodo} / ${row.cobranza}` : (row.periodo || ''),
      row.cnc || '',
      row.formaPago || '',
      row.estado_pago || '',
      row.folio || '',
      row.observaciones || '',
      (Number(row.cargo) || 0).toFixed(2),
      (Number(row.recargo) || 0).toFixed(2),
      (Number(row.comision) || 0).toFixed(2),
      (Number(row.importe) || 0).toFixed(2),
    ];

     // Ahora todas las columnas permiten saltos de línea automáticos
     const cellLines = cellData.map((text, i) => {
       return doc.splitTextToSize(String(text), cols[i].width - 2 * cellPadding);
     });
    // Calcula la altura máxima de la fila
    const maxLines = Math.max(...cellLines.map(lines => lines.length));
    const cellHeight = maxLines * 14; // 14pt por línea para mayor separación
    // Centramos verticalmente el bloque de texto en la celda
    const lineHeight = 14; // Debe coincidir con cellHeight/maxLines
    const y = currentY + (cellHeight - (maxLines * lineHeight)) / 2 + lineHeight - 4;

    // Escribe cada celda, línea por línea
     for (let lineIdx = 0; lineIdx < maxLines; lineIdx++) {
       x = margin + 4;
       for (let i = 0; i < cellData.length; i++) {
         const lines = cellLines[i];
         const txt = lines[lineIdx] || '';
          const align = i >= cellData.length - 4 ? 'right' : 'left'; // Números a la derecha (ahora 4 columnas numéricas)
         const yText = y + lineIdx * lineHeight;
         if (align === 'right') {
           doc.text(txt, x + cols[i].width - cellPadding, yText, { align: 'right', baseline: 'middle' });
         } else {
           doc.text(txt, x + cellPadding, yText, { baseline: 'middle' });
         }
         x += cols[i].width;
       }
     }
    // línea separadora
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y + cellHeight - 4, margin + usableWidth, y + cellHeight - 4);
    currentY += cellHeight;
  };

  // Iterate rows and paginate
  for (let i = 0; i < rows.length; i++) {
    if (currentY + rowHeight > maxY) {
      // footer: page number
      const pNum = `Página ${doc.getNumberOfPages()}`;
      doc.setFontSize(8);
      doc.text(pNum, pageWidth - margin, pageHeight - margin / 2, { align: 'right' });
      doc.addPage();
      currentY = drawHeader(true);
      doc.setFont('helvetica', 'normal'); // Restablece fuente normal para los datos
      doc.setFontSize(7);
    }
    drawRow(rows[i], i);
  }

  // Espacio extra antes de los totales
  currentY += 40;

  // Calcular espacio requerido para totales
  const totalesSeccionHeight = 18 + (totalesPorSeccion.length * 14) + 30; // encabezado + filas + margen
  const totalesSerieHeight = 18 + (totalesPorSerie.length * 14) + 30;
  const totalesConceptoHeight = Array.isArray(totalesPorConcepto) && totalesPorConcepto.length > 0 ? (22 + (totalesPorConcepto.length * 14)) : 0;
  const espacioTotales = Math.max(totalesSeccionHeight, totalesSerieHeight) + totalesConceptoHeight + 60;

  // Si no hay suficiente espacio, salto de página
  if (currentY + espacioTotales > pageHeight - margin - footerHeight) {
    // footer: page number
    const pNum = `Página ${doc.getNumberOfPages()}`;
    doc.setFontSize(8);
    doc.text(pNum, pageWidth - margin, pageHeight - margin / 2, { align: 'right' });
    doc.addPage();
    currentY = drawHeader(false);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
  }

  // --- Totales por Sección ---
  let yStart = currentY;
  const tableWidth = (pageWidth - margin * 2) / 2 - 10;
  const colWidths = [90, 70, 70, 70, 80]; // Sección/Serie, Cargo, Recargo, Comisión, Total

  doc.setFont('helvetica', 'bold');
  doc.text('TOTALES POR SECCIÓN', margin, yStart);
  doc.setFont('helvetica', 'normal');
  let x = margin;
  let y = yStart + 18;
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y - 10, colWidths.reduce((a, b) => a + b, 0), 16, 'F');
  ['Sección', 'Cargo', 'Recargo', 'Comisión', 'Total'].forEach((h, i) => {
    doc.setFont('helvetica', 'bold');
    if (i > 0) {
      doc.text(h, x + colWidths[i], y, { align: 'right' });
    } else {
      doc.text(h, x, y);
    }
    x += colWidths[i];
  });
  y += 12;
  doc.setFont('helvetica', 'normal');
  totalesPorSeccion.forEach((t) => {
    x = margin;
    doc.text(String(t.seccion), x, y);
    x += colWidths[0];
    doc.text(`$${Number(t.cargo || 0).toFixed(2)}`, x + colWidths[1], y, { align: 'right' });
    x += colWidths[1];
    doc.text(`$${Number(t.recargo || 0).toFixed(2)}`, x + colWidths[2], y, { align: 'right' });
    x += colWidths[2];
    doc.text(`$${Number(t.comision || 0).toFixed(2)}`, x + colWidths[3], y, { align: 'right' });
    x += colWidths[3];
    doc.text(`$${Number(t.total || t.cargo || 0).toFixed(2)}`, x + colWidths[4], y, { align: 'right' });
    y += 14;
  });

  // --- Totales por Serie ---
  let xSerie = margin + tableWidth + 40;
  let ySerie = yStart;
  doc.setFont('helvetica', 'bold');
  doc.text('TOTALES POR SERIE', xSerie, ySerie);
  doc.setFont('helvetica', 'normal');
  let x2 = xSerie;
  let y2 = ySerie + 18;
  doc.setFillColor(240, 240, 240);
  doc.rect(xSerie, y2 - 10, colWidths.reduce((a, b) => a + b, 0), 16, 'F');
  ['Serie', 'Cargo', 'Recargo', 'Comisión', 'Total'].forEach((h, i) => {
    doc.setFont('helvetica', 'bold');
    if (i > 0) {
      doc.text(h, x2 + colWidths[i], y2, { align: 'right' });
    } else {
      doc.text(h, x2, y2);
    }
    x2 += colWidths[i];
  });
  y2 += 12;
  doc.setFont('helvetica', 'normal');
  totalesPorSerie.forEach((t) => {
    x2 = xSerie;
    doc.text(String(t.serie), x2, y2);
    x2 += colWidths[0];
    doc.text(`$${Number(t.cargo || 0).toFixed(2)}`, x2 + colWidths[1], y2, { align: 'right' });
    x2 += colWidths[1];
    doc.text(`$${Number(t.recargo || 0).toFixed(2)}`, x2 + colWidths[2], y2, { align: 'right' });
    x2 += colWidths[2];
    doc.text(`$${Number(t.comision || 0).toFixed(2)}`, x2 + colWidths[3], y2, { align: 'right' });
    x2 += colWidths[3];
    doc.text(`$${Number(t.total || t.cargo || 0).toFixed(2)}`, x2 + colWidths[4], y2, { align: 'right' });
    y2 += 14;
  });

  // --- Totales por Concepto ---
  if (Array.isArray(totalesPorConcepto) && totalesPorConcepto.length > 0) {
    let yConcepto = Math.max(y, y2) + 30;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTALES POR CONCEPTO', margin, yConcepto);
    yConcepto += 10;
    doc.setFont('helvetica', 'bold');
    // Distribución de anchos (debe sumar <= usableWidth). Ajustada para dejar espacio a la columna Total
    const conceptoW = Math.floor(usableWidth * 0.26);
    let nombreConceptoW = Math.floor(usableWidth * 0.30);
    const cargoW = Math.floor(usableWidth * 0.12);
    const recargoW = Math.floor(usableWidth * 0.12);
    const comisionW = Math.floor(usableWidth * 0.10);
    let totalW = usableWidth - conceptoW - nombreConceptoW - cargoW - recargoW - comisionW;
    // Asegurar un ancho mínimo razonable para la columna Total
    const minTotalW = 60; // pts
    if (totalW < minTotalW) {
      const deficit = minTotalW - totalW;
      totalW = minTotalW;
      // Reducir ligeramente la columna de nombre de concepto si es posible
      const reduceFromNombre = Math.min(deficit, nombreConceptoW - 80);
      if (reduceFromNombre > 0) {
        nombreConceptoW -= reduceFromNombre;
        totalW -= 0; // totalW ya es minTotalW
      }
    }
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yConcepto - 8, usableWidth, 16, 'F');
    let xConcepto = margin;
    let xNombreConcepto = xConcepto + conceptoW;
    let xCargo = xNombreConcepto + nombreConceptoW;
    let xRecargo = xCargo + cargoW;
    let xComision = xRecargo + recargoW;
    let xTotal = xComision + comisionW;
    doc.text('Concepto', xConcepto + 4, yConcepto);
    doc.text('Nombre concepto', xNombreConcepto + 4, yConcepto);
    doc.text('Cargo', xCargo + cargoW - 4, yConcepto, { align: 'right' });
    doc.text('Recargo', xRecargo + recargoW - 4, yConcepto, { align: 'right' });
    doc.text('Comisión', xComision + comisionW - 4, yConcepto, { align: 'right' });
    doc.text('Total', xTotal + totalW - 4, yConcepto, { align: 'right' });
    yConcepto += 12;
    doc.setFont('helvetica', 'normal');
    totalesPorConcepto.forEach((row) => {
      const pad = 4;
      const conceptoLines = doc.splitTextToSize(String(row.concepto || ''), conceptoW - pad * 2);
      const nombreLines = doc.splitTextToSize(String(row.nombre_concepto || ''), nombreConceptoW - pad * 2);
      const maxLines = Math.max(conceptoLines.length, nombreLines.length, 1);
      // escribir líneas de 'Concepto' y 'Nombre concepto'
      for (let li = 0; li < maxLines; li++) {
        const yLine = yConcepto + li * 14;
        doc.text(conceptoLines[li] || '', xConcepto + pad, yLine);
        doc.text(nombreLines[li] || '', xNombreConcepto + pad, yLine);
      }
      // Alinear numéricos verticalmente en la primera línea del bloque
      doc.text(`$${Number(row.cargo || 0).toLocaleString('es-MX', {minimumFractionDigits:2})}`, xCargo + cargoW - 4, yConcepto, { align: 'right' });
      doc.text(`$${Number(row.recargo || 0).toLocaleString('es-MX', {minimumFractionDigits:2})}`, xRecargo + recargoW - 4, yConcepto, { align: 'right' });
      doc.text(`$${Number(row.comision || 0).toLocaleString('es-MX', {minimumFractionDigits:2})}`, xComision + comisionW - 4, yConcepto, { align: 'right' });
      doc.text(`$${Number(row.total || row.cargo || 0).toLocaleString('es-MX', {minimumFractionDigits:2})}`, xTotal + totalW - 4, yConcepto, { align: 'right' });
      yConcepto += maxLines * 14;
    });
  }

  if (descargar) {
    doc.save(`${title.replace(/\s+/g, '_') || 'reporte'}.pdf`);
  }
  return doc.output('datauristring');
}
